/**
 * Requires-graph extraction and cycle detection.
 *
 * Extracts cross-asset dependency edges from typed manifest fields:
 *   - agent:  skills[], composes[].ref, extends
 *   - flow:   nodes[].run.assets, with legacy data.skill / data.flow fallbacks
 *   - preset: items[].ref, items[].composes[].ref
 *   - skill:  metadata.artifacts.requires[].id
 *
 * Cycle detection uses Kahn's algorithm (topological sort).
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * A directed dependency edge from one asset ref to another, annotated with the manifest
 * field that declared the dependency.
 *
 * @docLink packages/discovery/concepts#requires-edge
 */
export interface RequiresEdge {
  /** Asset ref of the requiring asset. */
  from: string;
  /** Asset ref of the required asset. */
  to: string;
  /** Manifest field that produced this edge. */
  field: string;
}

/**
 * The complete requires graph for a discovered asset tree: all dependency edges plus
 * the result of cycle detection.
 *
 * @docLink packages/discovery/concepts#requires-graph
 */
export interface RequiresGraph {
  edges: RequiresEdge[];
  /** True if the graph contains no cycles. */
  acyclic: boolean;
  /** If cycles exist, one example cycle path. */
  cyclePath?: string[];
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Extract requires edges from a single asset's manifest.
 *
 * @param assetRef - The canonical ref of the asset being examined (the `from` side)
 * @param kind     - Asset kind, used to select the correct manifest field extractor
 * @param manifest - Parsed manifest object
 * @returns Array of {@link RequiresEdge} declared by this asset
 * @docLink packages/discovery/concepts#extract-requires
 */
export function extractRequires(
  assetRef: string,
  kind: string,
  manifest: Record<string, unknown>,
): RequiresEdge[] {
  const edges: RequiresEdge[] = [];

  switch (kind) {
    case "agent":
      extractAgentRequires(assetRef, manifest, edges);
      break;
    case "flow":
      extractFlowRequires(assetRef, manifest, edges);
      break;
    case "preset":
      extractPresetRequires(assetRef, manifest, edges);
      break;
    case "skill":
      extractSkillRequires(assetRef, manifest, edges);
      break;
    // connector, mount, prompt, mcp-server, contract, knowledge, persona, ruleset:
    // no cross-asset requires in their manifest schemas.
  }

  return edges;
}

/**
 * Build a complete {@link RequiresGraph} from multiple assets' edges by running cycle detection.
 *
 * @param allEdges - All {@link RequiresEdge} values collected across all assets in the tree
 * @returns A {@link RequiresGraph} with `acyclic` status and optional `cyclePath`
 * @docLink packages/discovery/concepts#build-requires-graph
 */
export function buildRequiresGraph(allEdges: RequiresEdge[]): RequiresGraph {
  const { acyclic, cyclePath } = detectCycles(allEdges);
  return { edges: allEdges, acyclic, cyclePath };
}

/**
 * Detect cycles in a set of requires edges using Kahn's topological sort algorithm.
 *
 * If a cycle is found, DFS is used to recover one example cycle path for diagnostics.
 *
 * @param edges - Directed dependency edges to analyse
 * @returns `{ acyclic: true }` if the graph is a DAG, or `{ acyclic: false, cyclePath }` otherwise
 * @docLink packages/discovery/concepts#detect-cycles
 */
export function detectCycles(edges: RequiresEdge[]): { acyclic: boolean; cyclePath?: string[] } {
  if (edges.length === 0) return { acyclic: true };

  // Build adjacency + in-degree
  const nodes = new Set<string>();
  const adj = new Map<string, Set<string>>();
  const inDegree = new Map<string, number>();

  for (const e of edges) {
    nodes.add(e.from);
    nodes.add(e.to);
    if (!adj.has(e.from)) adj.set(e.from, new Set());
    const neighbors = adj.get(e.from)!;
    if (!neighbors.has(e.to)) {
      neighbors.add(e.to);
      inDegree.set(e.to, (inDegree.get(e.to) ?? 0) + 1);
    }
    if (!inDegree.has(e.from)) inDegree.set(e.from, 0);
  }

  // Kahn's algorithm
  const queue: string[] = [];
  for (const node of nodes) {
    if ((inDegree.get(node) ?? 0) === 0) queue.push(node);
  }

  const sorted: string[] = [];
  while (queue.length > 0) {
    const n = queue.shift()!;
    sorted.push(n);
    for (const neighbor of adj.get(n) ?? []) {
      const deg = inDegree.get(neighbor)! - 1;
      inDegree.set(neighbor, deg);
      if (deg === 0) queue.push(neighbor);
    }
  }

  if (sorted.length === nodes.size) {
    return { acyclic: true };
  }

  // Find one cycle path via DFS among remaining nodes
  const remaining = new Set([...nodes].filter((n) => !sorted.includes(n)));
  const cyclePath = findCyclePath(remaining, adj);
  return { acyclic: false, cyclePath };
}

// ---------------------------------------------------------------------------
// Per-kind extractors
// ---------------------------------------------------------------------------

function extractAgentRequires(
  ref: string,
  m: Record<string, unknown>,
  edges: RequiresEdge[],
): void {
  // skills: string[]
  if (Array.isArray(m.skills)) {
    for (const s of m.skills) {
      if (typeof s === "string") edges.push({ from: ref, to: s, field: "skills" });
    }
  }
  // composes: { ref: string, ... }[]
  if (Array.isArray(m.composes)) {
    for (const c of m.composes) {
      if (isObjectWithStringField(c, "ref")) {
        edges.push({ from: ref, to: (c as Record<string, string>).ref!, field: "composes" });
      }
    }
  }
  // extends: string
  if (typeof m.extends === "string") {
    edges.push({ from: ref, to: m.extends, field: "extends" });
  }
}

function extractFlowRequires(ref: string, m: Record<string, unknown>, edges: RequiresEdge[]): void {
  if (!Array.isArray(m.nodes)) return;
  const seen = new Set<string>();
  const add = (to: string, field: string): void => {
    if (seen.has(to)) return;
    seen.add(to);
    edges.push({ from: ref, to, field });
  };

  for (const node of m.nodes) {
    if (typeof node !== "object" || node === null) continue;
    const n = node as Record<string, unknown>;
    const data =
      typeof n.data === "object" && n.data !== null ? (n.data as Record<string, unknown>) : {};
    const run =
      typeof n.run === "object" && n.run !== null ? (n.run as Record<string, unknown>) : {};

    if (Array.isArray(run.assets)) {
      for (const asset of run.assets) {
        if (typeof asset === "string") add(asset, "nodes[].run.assets");
      }
    }

    if (run.kind === "sub-flow" && typeof run.flow === "string") {
      const flowRef = run.flow.startsWith("flow:") ? run.flow : `flow:${run.flow}`;
      add(flowRef, "nodes[].run.flow");
    }

    const skill = typeof data.skill === "string" ? data.skill : n.skill;
    if (typeof skill === "string") {
      add(skill, "nodes[].data.skill");
    }

    const subFlow = typeof data.flow === "string" ? data.flow : n.flow;
    if (typeof subFlow === "string") {
      add(subFlow.startsWith("flow:") ? subFlow : `flow:${subFlow}`, "nodes[].data.flow");
    }
  }
}

function extractPresetRequires(
  ref: string,
  m: Record<string, unknown>,
  edges: RequiresEdge[],
): void {
  if (!Array.isArray(m.items)) return;
  for (const item of m.items) {
    if (typeof item !== "object" || item === null) continue;
    const i = item as Record<string, unknown>;

    // items[].ref — skip library:// refs (those reference existing instances, not assets)
    if (typeof i.ref === "string" && !i.ref.startsWith("library://")) {
      edges.push({ from: ref, to: i.ref, field: "items[].ref" });
    }

    // items[].composes[].ref — skip # (cross-item) and library:// refs
    if (Array.isArray(i.composes)) {
      for (const c of i.composes) {
        if (!isObjectWithStringField(c, "ref")) continue;
        const cRef = (c as Record<string, string>).ref!;
        if (!cRef.startsWith("#") && !cRef.startsWith("library://")) {
          edges.push({ from: ref, to: cRef, field: "items[].composes[].ref" });
        }
      }
    }
  }
}

function extractSkillRequires(
  ref: string,
  m: Record<string, unknown>,
  edges: RequiresEdge[],
): void {
  const metadata = m.metadata as Record<string, unknown> | undefined;
  if (!metadata) return;
  const artifacts = metadata.artifacts as Record<string, unknown> | undefined;
  if (!artifacts) return;
  if (!Array.isArray(artifacts.requires)) return;
  for (const r of artifacts.requires) {
    if (isObjectWithStringField(r, "id")) {
      edges.push({
        from: ref,
        to: (r as Record<string, string>).id!,
        field: "metadata.artifacts.requires",
      });
    }
  }
}

// ---------------------------------------------------------------------------
// Cycle finder (DFS)
// ---------------------------------------------------------------------------

function findCyclePath(nodes: Set<string>, adj: Map<string, Set<string>>): string[] {
  const visited = new Set<string>();
  const stack = new Set<string>();
  const path: string[] = [];

  for (const start of nodes) {
    if (visited.has(start)) continue;
    const cycle = dfs(start, adj, visited, stack, path, nodes);
    if (cycle) return cycle;
  }

  return [...nodes]; // fallback: return all remaining nodes
}

function dfs(
  node: string,
  adj: Map<string, Set<string>>,
  visited: Set<string>,
  stack: Set<string>,
  path: string[],
  scope: Set<string>,
): string[] | null {
  visited.add(node);
  stack.add(node);
  path.push(node);

  for (const neighbor of adj.get(node) ?? []) {
    if (!scope.has(neighbor)) continue;
    if (stack.has(neighbor)) {
      // Found cycle — extract the cycle portion
      const cycleStart = path.indexOf(neighbor);
      return [...path.slice(cycleStart), neighbor];
    }
    if (!visited.has(neighbor)) {
      const result = dfs(neighbor, adj, visited, stack, path, scope);
      if (result) return result;
    }
  }

  stack.delete(node);
  path.pop();
  return null;
}

// ---------------------------------------------------------------------------
// Utility
// ---------------------------------------------------------------------------

function isObjectWithStringField(val: unknown, field: string): boolean {
  return (
    typeof val === "object" &&
    val !== null &&
    field in val &&
    typeof (val as Record<string, unknown>)[field] === "string"
  );
}
